// A person's avatar for the list screens: the deterministic mercury-avatar when
// a seed is known, else a neutral initial disc. Optionally overlays a presence
// pip (bottom-right, ringed in the card colour so it reads as cut into the
// avatar). The mercury pixels come only from the frozen renderer (MercuryAvatar
// → mercury.ts); this never draws its own.

import { type ReactNode } from 'react';
import type { PresenceStatus } from '@merkury/protocol';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';

/** First visible character of a name, uppercased — the initial-disc glyph. */
export function initialOf(name: string): string {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : '?';
}

const PIP_CLASS: Record<PresenceStatus, string> = {
  app: 'bg-ok',
  reachable: 'bg-warn',
  offline: 'bg-muted',
};

export interface PeerAvatarProps {
  userId: string;
  name: string;
  /** The friend's numeric avatar seed; composed with userId. Undefined ⇒ initial disc. */
  avatarSeed?: number;
  /** When set, a presence pip is drawn in the corner. Omit for contexts where
   *  presence is meaningless (e.g. a call-log entry). */
  presence?: PresenceStatus;
  /** CSS px, square. */
  size?: number;
}

export function PeerAvatar({
  userId,
  name,
  avatarSeed,
  presence,
  size = 44,
}: PeerAvatarProps): ReactNode {
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      {avatarSeed !== undefined ? (
        <MercuryAvatar seed={candidateSeed(userId, avatarSeed)} size={size} className="rounded-full" />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center rounded-full bg-raised font-semibold text-muted"
          style={{ fontSize: Math.round(size * 0.38) }}
        >
          {initialOf(name)}
        </span>
      )}
      {presence && (
        <span
          aria-hidden="true"
          data-presence={presence}
          className={`absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-surface ${PIP_CLASS[presence]}`}
        />
      )}
    </span>
  );
}
